import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  updateDoc,
  deleteDoc,
  doc,
  writeBatch,
  addDoc,
  getDocs,
  getDoc,
  Timestamp,
  limit as firestoreLimit,
  type Unsubscribe,
} from '@/lib/firestore'
import { db } from '@/config/firebase'
import { useAuthStore } from '@/stores/auth'
import { getTenantConstraint, getTenantData } from '@/utils/tenantQuery'
import {
  NOTIFICATION_TYPE_TO_CATEGORY,
  type NotificationType,
  type NotificationEntityType,
  type NotificationPreferences,
} from '@/types'
import type { NotificationsState } from './state'

export const createActions = (state: NotificationsState) => {
  let unsubscribe: Unsubscribe | null = null
  let previousIds: Set<string> = new Set()
  let onNewNotificationCallback: ((title: string, message: string) => void) | null = null

  const setOnNewNotification = (cb: ((title: string, message: string) => void) | null) => {
    onNewNotificationCallback = cb
  }

  const subscribe = (): Unsubscribe | null => {
    const authStore = useAuthStore()
    const userId = authStore.userId
    if (!userId || state.isSubscribed) return null

    state.isLoading = true
    state.isSubscribed = true

    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      firestoreLimit(50)
    )

    unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const newNotifications = snapshot.docs.map((d) => {
          const docData = d.data()
          return {
            id: d.id,
            ...docData,
            // Bolsa de datos accionable (p.ej. approve_pipeline): explícita para que no se pierda.
            data: docData.data,
            createdAt: docData.createdAt?.toDate() || new Date(),
          }
        }) as import('@/types').Notification[]

        // Detect truly new notifications (for toast)
        if (previousIds.size > 0 && onNewNotificationCallback) {
          for (const n of newNotifications) {
            if (!previousIds.has(n.id) && !n.isRead) {
              onNewNotificationCallback(n.title, n.message)
            }
          }
        }

        previousIds = new Set(newNotifications.map((n) => n.id))
        state.notifications = newNotifications
        state.isLoading = false
      },
      (err) => {
        // eslint-disable-next-line no-console
        console.error('Notifications subscribe error:', err)
        state.error = err.message
        state.isLoading = false
        state.isSubscribed = false
      }
    )

    return unsubscribe
  }

  const cleanup = (): void => {
    if (unsubscribe) {
      unsubscribe()
      unsubscribe = null
    }
    state.notifications = []
    state.isSubscribed = false
    previousIds = new Set()
  }

  const markAsRead = async (notificationId: string): Promise<void> => {
    try {
      await updateDoc(doc(db, 'notifications', notificationId), { isRead: true })
    } catch (err) {
      state.error = err instanceof Error ? err.message : 'Error al marcar como leida'
      throw err
    }
  }

  const markAllAsRead = async (): Promise<void> => {
    const authStore = useAuthStore()
    if (!authStore.userId) return

    try {
      const unreadNotifications = state.notifications.filter((n) => !n.isRead)
      if (unreadNotifications.length === 0) return

      const batch = writeBatch(db)
      unreadNotifications.forEach((notification) => {
        batch.update(doc(db, 'notifications', notification.id), { isRead: true })
      })
      await batch.commit()
    } catch (err) {
      state.error = err instanceof Error ? err.message : 'Error al marcar todas como leidas'
      throw err
    }
  }

  const deleteNotification = async (notificationId: string): Promise<void> => {
    try {
      await deleteDoc(doc(db, 'notifications', notificationId))
    } catch (err) {
      state.error = err instanceof Error ? err.message : 'Error al eliminar notificacion'
      throw err
    }
  }

  const deleteAllRead = async (): Promise<void> => {
    try {
      const readNotifications = state.notifications.filter((n) => n.isRead)
      if (readNotifications.length === 0) return

      const batch = writeBatch(db)
      readNotifications.forEach((notification) => {
        batch.delete(doc(db, 'notifications', notification.id))
      })
      await batch.commit()
    } catch (err) {
      state.error = err instanceof Error ? err.message : 'Error al eliminar notificaciones'
      throw err
    }
  }

  const isNotificationEnabled = (
    preferences: NotificationPreferences | undefined,
    type: NotificationType
  ): boolean => {
    if (!preferences) return true // Default: all enabled
    const category = NOTIFICATION_TYPE_TO_CATEGORY[type]
    if (preferences[category] === false) return false
    if (preferences.typeOverrides?.[type] === false) return false
    return true
  }

  const createNotification = async (
    userId: string,
    type: NotificationType,
    title: string,
    message: string,
    entityType?: NotificationEntityType,
    entityId?: string
  ): Promise<void> => {
    try {
      const authStore = useAuthStore()
      // No notificar al usuario que realizo la accion
      if (userId === authStore.userId) return

      await addDoc(collection(db, 'notifications'), {
        ...getTenantData(),
        userId,
        type,
        title,
        message,
        entityType,
        entityId,
        isRead: false,
        createdAt: Timestamp.now(),
        createdBy: authStore.userId || '',
      })
    } catch (err) {
      console.error('Error creating notification:', err)
    }
  }

  const notifyAdminsAndOperators = async (
    type: NotificationType,
    title: string,
    message: string,
    entityType?: NotificationEntityType,
    entityId?: string
  ): Promise<void> => {
    try {
      const authStore = useAuthStore()
      const tenantConstraint = getTenantConstraint()
      const userConstraints = tenantConstraint
        ? [tenantConstraint, where('role', 'in', ['admin', 'operator']), where('isActive', '==', true)]
        : [where('role', 'in', ['admin', 'operator']), where('isActive', '==', true)]
      const usersQuery = query(collection(db, 'users'), ...userConstraints)
      const snapshot = await getDocs(usersQuery)

      const tenantData = getTenantData()
      const currentUserId = authStore.userId
      const batch = writeBatch(db)
      snapshot.docs.forEach((userDoc) => {
        // No notificar al usuario que realizo la accion
        if (userDoc.id === currentUserId) return

        const userData = userDoc.data()
        // Respect user notification preferences
        if (!isNotificationEnabled(userData.notificationPreferences, type)) return

        const notificationRef = doc(collection(db, 'notifications'))
        batch.set(notificationRef, {
          ...tenantData,
          userId: userDoc.id,
          type,
          title,
          message,
          entityType,
          entityId,
          isRead: false,
          createdAt: Timestamp.now(),
          createdBy: currentUserId || '',
        })
      })
      await batch.commit()
    } catch (err) {
      console.error('Error notifying admins/operators:', err)
    }
  }

  const notifyAdminsAndStakeholders = async (
    stakeholderIds: string[],
    type: NotificationType,
    title: string,
    message: string,
    entityType?: NotificationEntityType,
    entityId?: string
  ): Promise<void> => {
    try {
      const authStore = useAuthStore()
      const tenantConstraint = getTenantConstraint()
      const currentUserId = authStore.userId

      // Query only admins (not operators) for oversight
      const adminConstraints = tenantConstraint
        ? [tenantConstraint, where('role', '==', 'admin'), where('isActive', '==', true)]
        : [where('role', '==', 'admin'), where('isActive', '==', true)]
      const adminsQuery = query(collection(db, 'users'), ...adminConstraints)
      const snapshot = await getDocs(adminsQuery)

      // Merge admin IDs with stakeholder IDs, dedup
      const adminIds = snapshot.docs.map((d) => d.id)
      const allUserIds = [...new Set([...adminIds, ...stakeholderIds])]

      // Build a map of admin preferences for quick lookup
      const adminPrefs = new Map<string, NotificationPreferences | undefined>()
      snapshot.docs.forEach((d) => {
        adminPrefs.set(d.id, d.data().notificationPreferences)
      })

      // For stakeholders that are not admins, fetch their preferences
      const nonAdminStakeholders = stakeholderIds.filter((id) => !adminPrefs.has(id))
      if (nonAdminStakeholders.length > 0) {
        for (const uid of nonAdminStakeholders) {
          try {
            const userDoc = await getDoc(doc(db, 'users', uid))
            if (userDoc.exists()) {
              adminPrefs.set(uid, userDoc.data().notificationPreferences)
            }
          } catch {
            // Skip if user not found
          }
        }
      }

      const tenantData = getTenantData()
      const batch = writeBatch(db)

      for (const userId of allUserIds) {
        if (userId === currentUserId) continue
        if (!isNotificationEnabled(adminPrefs.get(userId), type)) continue

        const notificationRef = doc(collection(db, 'notifications'))
        batch.set(notificationRef, {
          ...tenantData,
          userId,
          type,
          title,
          message,
          entityType,
          entityId,
          isRead: false,
          createdAt: Timestamp.now(),
          createdBy: currentUserId || '',
        })
      }

      await batch.commit()
    } catch (err) {
      console.error('Error notifying admins and stakeholders:', err)
    }
  }

  const notifyUsers = async (
    userIds: string[],
    type: NotificationType,
    title: string,
    message: string,
    entityType?: NotificationEntityType,
    entityId?: string
  ): Promise<void> => {
    try {
      const authStore = useAuthStore()
      const currentUserId = authStore.userId
      const tenantData = getTenantData()

      // Dedup userIds
      const uniqueUserIds = [...new Set(userIds)]

      // Fetch preferences for all target users
      const userPrefs = new Map<string, NotificationPreferences | undefined>()
      for (const uid of uniqueUserIds) {
        if (uid === currentUserId) continue
        try {
          const userDoc = await getDoc(doc(db, 'users', uid))
          if (userDoc.exists()) {
            userPrefs.set(uid, userDoc.data().notificationPreferences)
          }
        } catch {
          // Skip if user not found
        }
      }

      const batch = writeBatch(db)

      for (const userId of uniqueUserIds) {
        // No notificar al usuario que realizo la accion
        if (userId === currentUserId) continue
        if (!isNotificationEnabled(userPrefs.get(userId), type)) continue

        const notificationRef = doc(collection(db, 'notifications'))
        batch.set(notificationRef, {
          ...tenantData,
          userId,
          type,
          title,
          message,
          entityType,
          entityId,
          isRead: false,
          createdAt: Timestamp.now(),
          createdBy: currentUserId || '',
        })
      }

      await batch.commit()
    } catch (err) {
      console.error('Error notifying users:', err)
    }
  }

  return {
    subscribe,
    cleanup,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllRead,
    createNotification,
    notifyAdminsAndOperators,
    notifyAdminsAndStakeholders,
    notifyUsers,
    setOnNewNotification,
  }
}

export type NotificationsActions = ReturnType<typeof createActions>
